import {
    Component,
    NgZone,
    OnInit,
    ViewEncapsulation,
    computed,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { find } from 'lodash-es';
import { EventBusService } from 'apps/account/src/account-shared/services/event-bus.service';
import { CampaignV1ApiService } from 'apps/account/src/account-shared/services/campaign-v1-api.service';
import { IncentivesV1ApiService } from 'apps/account/src/account-shared/services/incentives-v1-api.service';
import { DEFAULT_LANGUAGE, languageHelper } from 'apps/account/src/account-shared/services/language-helper.util';
import { TranslateModule } from '@ngx-translate/core';
import { FtCampaignGoalsComponent } from './ft-campaign-goals.component';
import { FtEditCampaignGoalsComponent, EditGoalsTitlePipe } from './ft-edit-campaign-goals.component';
import { FtPendingChangesPanelComponent } from './ft-pending-changes-panel.component';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { computeDiffs, revertDiffs, snapshotClone } from '../contest-sheets/native-diff.util';

/**
 * Native port of the goals-only campaign editors (post-stay + photo-advocacy). These layers have
 * just a GOALS section plus the deferred-save panel — no info/criteria/share-message. Serves both
 * post-stay and photo-advocacy editing (routed here by the container's `nativeBranch()`); loads its
 * own data (Campaign.get + Incentives.getAvailableIncentives). Photo-advocacy's Save-enable rule is
 * reproduced in {@link panelInactive}; post-stay has no such rule (always enabled).
 */
@Component({
    selector: 'ft-campaign-goals-only-ng',
    imports: [
        TranslateModule,
        FtCampaignGoalsComponent,
        FtEditCampaignGoalsComponent,
        EditGoalsTitlePipe,
        FtPendingChangesPanelComponent,
        FtModalComponent,
        FtModalChromeComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./ft-campaign-goals-only.component.scss'],
    templateUrl: './ft-campaign-goals-only.component.html'
})
export class FtCampaignGoalsOnlyComponent implements OnInit {
    readonly campaignId = input.required<string>();
    readonly layerName = input.required<string>();
    readonly propertyUuid = input.required<string>();
    readonly isCreating = input(false);

    private readonly panel = viewChild<FtPendingChangesPanelComponent>('panel');

    protected readonly model = signal<any>({ campaign: undefined, languages: [], isCreating: false });
    protected readonly selectedLanguage = signal<any>({});
    protected readonly propertyIncentives = signal<any[]>([]);
    /** Set by onEditGoals (native read-view click) → bound by the NATIVE goals modal. */
    protected readonly types = signal<number[]>([]);
    protected readonly incentiveTag = signal<any>(undefined);
    /** ftModal phase 2 / WS3: the native campaign goals modal open flag + its goals.item snapshot. */
    protected readonly goalsModalOpen = signal(false);
    private goalsSnapshot: any = null;

    private layerNameApi = '';

    // WS5 #10 inc 2s: fully drained — Campaign/Incentives are the native V1Api
    // services, languageHelper/_ the native util/lodash (same module instances).
    private readonly Campaign = inject(CampaignV1ApiService);
    private readonly Incentives = inject(IncentivesV1ApiService);
    private readonly languageHelper = languageHelper;
    // WS5 #10 inc 2i: 'campaign.changed' is native↔native (listeners = the native
    // ft-campaign-container header and the journeys and audiences pages) → the native event bus, not $rootScope.
    private readonly eventBus = inject(EventBusService);

    private readonly zone = inject(NgZone);

    /** Per-layer GOALS summary key (post-stay uses the preStay key; photo-advocacy its own). */
    protected readonly goalsSummaryKey = computed<string>(() =>
        this.layerName() === 'photo-advocacy'
            ? 'flipto.account.platform.photoAdvocacy.campaign.GoalsSummary'
            : 'flipto.account.platform.preStay.campaign.GoalsSummary',
    );

    /**
     * The native pending-changes panel `[inactive]` (disables Save). Reproduces the v1 panel `inactive`:
     *   - photo-advocacy: `!(campaignForm.$valid && (type==255 || campaignTypeId==6 || criteria*.$valid))`.
     *     campaignForm has only a NON-required input → always $valid; photo-advocacy.html has no criteria
     *     `<ng-form>`s → those terms are undefined/falsy. So it reduces to `!(type==255 || campaignTypeId==6)`.
     *   - post-stay: the v1 post-stay panel has NO `inactive` attr → always enabled → false.
     * type is int-converted by Campaign.setupCampaign; loose `==` matches v1.
     */
    protected get panelInactive(): boolean {
        if (this.layerName() !== 'photo-advocacy') return false;
        const c = this.model().campaign;
        if (!c) return false;
        return !(c.type == 255 || c.campaignTypeId == 6);
    }

    ngOnInit(): void {
        this.layerNameApi = (this.layerName() || '').replace('-', '');
        this.model.set({ campaignId: this.campaignId(), isCreating: !!this.isCreating(), campaign: undefined, languages: [] });
        this.activate();
    }

    /** Native read-view (FtCampaignGoalsComponent) click → set types/tag + open the NATIVE modal. */
    protected onEditGoals(e: { types: number[]; incentiveTag: number }): void {
        this.types.set(e.types);
        this.incentiveTag.set(e.incentiveTag);
        // v1 ftCampaignGoals.init ensured campaign.goals = campaign.goals || {item:[]}.
        if (this.model().campaign && !this.model().campaign.goals) this.model().campaign.goals = { item: [] };
        // Lazy panel snapshot at FIRST field-open (#52: the model is fully loaded by now).
        this.panel()?.setupIfNeeded();
        this.goalsSnapshot = snapshotClone(this.model().campaign && this.model().campaign.goals && this.model().campaign.goals.item);
        this.goalsModalOpen.set(true);
    }

    /**
     * Campaign goals modal Done — DEFERRED save (v1 on-done="saveGoals" is undefined): close, then
     * recompute the NATIVE pending-changes panel so it picks up the in-place goals.item change
     * (native-diff; no AngularJS digest — avoids the homepage trap).
     */
    protected onGoalsDone(): void {
        this.goalsModalOpen.set(false);
        this.panel()?.recompute();
    }

    /** Campaign goals modal X/Cancel — v1 ft-snapshot rollback: revert model.campaign.goals.item. */
    protected onGoalsCancel(): void {
        const live = this.model().campaign && this.model().campaign.goals && this.model().campaign.goals.item;
        if (live && this.goalsSnapshot) {
            revertDiffs(live, this.goalsSnapshot, computeDiffs(this.goalsSnapshot, live));
        }
        this.goalsModalOpen.set(false);
        this.panel()?.recompute();
    }

    /** Native pending-changes panel Save → the v1 deferred commit (PUT), then re-snapshot the panel. */
    protected onPanelSave(): void {
        this.commit();
    }

    /** Native pending-changes panel Cancel — the panel already reverted model.campaign in place. */
    protected onPanelCancel(): void {
        /* no-op: FtPendingChangesPanelComponent.onCancel reverted via revertDiffs (v1 rollback). */
    }

    private activate(): void {
        Promise.resolve(this.Campaign.get({ id: this.campaignId(), layerName: this.layerNameApi, propertyUuid: this.propertyUuid() })).then((response: any) =>
            this.zone.run(() => {
                this.prepareCampaignEditResponse(response);
                this.selectedLanguage.set(find(this.model().languages, { code: DEFAULT_LANGUAGE }) || this.model().languages[0] || {});
            }),
        );
        Promise.resolve(this.Incentives.getAvailableIncentives(this.propertyUuid())).then((response: any) =>
            this.zone.run(() => {
                this.propertyIncentives.set(response.data);
            }),
        );
    }

    private prepareCampaignEditResponse(response: any): void {
        this.model().campaign = response;
        this.model().languages = response.languages.item;
        this.ensureMessages(this.model().campaign, this.model().languages);
    }

    /** Mirror the CampaignController ensureMessages (post-stay/photo-advocacy: empty default). */
    private ensureMessages(campaign: any, languages: any[]): void {
        if (!campaign.messages || !campaign.messages.length) {
            const message: any = { isActive: 1, languages: [] };
            this.languageHelper.merge(message.languages, languages);
            campaign.messages = [message];
        }
    }

    reloadGoals(): void {
        this.activate();
    }

    /** The pending-changes deferred-save (triggered by the native panel's Save → it PUTs). */
    commit(): void {
        const params = { propertyUUID: this.propertyUuid(), layerName: this.layerNameApi, id: this.model().campaign.id };
        const campaign = this.model().campaign;
        Promise.resolve(this.Campaign.update(params, campaign))
            .then((response: any) => this.zone.run(() => this.prepareCampaignEditResponse(response)))
            .then(() => {
                // v1 snapshot.commit() (retake) → pendingChanges 0. Pass the FRESH campaign explicitly: the
                // panel's [live] input hasn't re-bound to the new response object yet (CD runs after this
                // microtask), so a bare commit() would re-snapshot the stale pre-save object → phantom changes.
                this.panel()?.commit(this.model().campaign);
                this.eventBus.emit('campaign.changed', campaign);
            });
    }

    // WS5 #10 inc 2j: the dead `cancel()` is REMOVED — it was never invoked (the template
    // wires `(cancel)="onGoalsCancel()"`) and its `ftModal.hide('accountv2.CampaignContainer')`
    // targeted an id that exists in NO registry (the v1 container ft-modal died with the
    // native container mount; v1 hide() is null-safe → it was a no-op even if called).
}
